import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useUpdateGroupMutation } from "../../infrastructure/api/membersGroupsApi";
import type { Group } from "../../domain/entities/Member";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group: Group | null;
}

export const EditGroupDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  group,
}) => {
  const { t } = useTranslation("membersGroups");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const [updateGroup, { isLoading }] = useUpdateGroupMutation();

  useEffect(() => {
    if (group) {
      setName(group.name ?? "");
      setDescription(group.description ?? "");
    }
  }, [group]);

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleSave = async () => {
    if (!group || !name.trim()) return;
    try {
      await updateGroup({
        groupId: group.id,
        name: name.trim(),
        description: description.trim() || undefined,
      }).unwrap();
      toast.success(t("editGroupDialog.success"));
      handleClose();
    } catch {
      toast.error(t("editGroupDialog.error"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="flex max-h-[calc(100dvh-1rem)] flex-col overflow-hidden rounded-xl p-0 sm:max-w-[500px]">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="text-lg font-semibold">
            {t("editGroupDialog.title")}
          </DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
          <div className="space-y-1">
            <label className="text-sm font-medium">
              {t("editGroupDialog.nameLabel")} <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder={t("editGroupDialog.namePlaceholder")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">{t("editGroupDialog.descriptionLabel")}</label>
            <Input
              placeholder={t("editGroupDialog.descriptionPlaceholder")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isLoading}
            />
          </div>
        </div>

        <DialogFooter className="flex shrink-0 justify-end gap-3 border-t px-6 py-4 pb-8">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
            className="border-primary text-primary hover:text-primary hover:bg-white cursor-pointer"
          >
            {t("editGroupDialog.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!name.trim()}
            loading={isLoading}
            className="bg-primary text-white hover:bg-primary cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("editGroupDialog.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
